import { useState, useEffect } from "react";
import { generateRandomUsername, getUsernameParts } from "../utils/username";

interface UseUsernameProps {
  isAuthenticated: boolean;
  userId?: string | null;
  hasJoinedRoom?: boolean;
}

export function useUsername({ isAuthenticated, userId, hasJoinedRoom }: UseUsernameProps) {
  // Initialize with separate text and number parts
  const [usernameText, setUsernameText] = useState(() => {
    // Only load username if authenticated
    if (!isAuthenticated || !userId) {
      return "";
    }
    
    const stored = localStorage.getItem(`BlinkBli:username:${userId}`);
    if (stored) {
      const { textPart } = getUsernameParts(stored);
      return textPart;
    }
    const newUsername = generateRandomUsername();
    const { textPart } = getUsernameParts(newUsername);
    localStorage.setItem(`BlinkBli:username:${userId}`, newUsername);
    return textPart;
  });

  const usernameNumber = (() => {
    // Only load username if authenticated
    if (!isAuthenticated || !userId) {
      return "";
    }
    
    const stored = localStorage.getItem(`BlinkBli:username:${userId}`);
    if (stored) {
      const { numberPart } = getUsernameParts(stored);
      return numberPart;
    }
    const newUsername = generateRandomUsername();
    const { numberPart } = getUsernameParts(newUsername);
    return numberPart;
  })();

  // Update username when authentication status changes
  useEffect(() => {
    if (isAuthenticated && userId) {
      const stored = localStorage.getItem(`BlinkBli:username:${userId}`);
      if (stored) {
        const { textPart } = getUsernameParts(stored);
        setUsernameText(textPart);
      } else {
        // Generate new username for authenticated user
        const newUsername = generateRandomUsername();
        const { textPart } = getUsernameParts(newUsername);
        setUsernameText(textPart);
        localStorage.setItem(`BlinkBli:username:${userId}`, newUsername);
      }
    } else {
      // Clear username when not authenticated
      setUsernameText("");
    }
  }, [isAuthenticated, userId]);

  const [nameHint, setNameHint] = useState<string | null>(null);
  
  // Combine for display purposes only
  const username = usernameNumber ? `${usernameText}.${usernameNumber}` : usernameText;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow username changes when authenticated AND has joined a room
    if (!isAuthenticated || !userId) {
      setNameHint("Please sign in to set your username");
      return;
    }

    if (!hasJoinedRoom) {
      setNameHint("Join a room first to set your username");
      return;
    }

    let value = e.target.value;
    
    // Remove spaces and special characters, keep only alphanumeric
    value = value.replace(/[^a-zA-Z0-9]/g, '');
    
    // Limit length to 20 characters
    if (value.length > 20) {
      value = value.substring(0, 20);
    }
    
    setUsernameText(value);
    
    // Update localStorage with the combined username (user-specific)
    const fullUsername = usernameNumber ? `${value}.${usernameNumber}` : value;
    localStorage.setItem(`BlinkBli:username:${userId}`, fullUsername);
    
    if (!value.trim()) {
      setNameHint("Name can't be blank");
    } else if (value.length < 2) {
      setNameHint("Name must be at least 2 characters");
    } else {
      setNameHint(`Welcome back, ${value} 👋`);
      setTimeout(() => setNameHint(null), 1500);
    }
  };

  return {
    username,
    usernameText,
    usernameNumber,
    nameHint,
    handleUsernameChange
  };
}
